import {
  Accordion,
  AccordionItem,
  Chip,
  DateInput,
  Input,
  Link,
} from "@heroui/react";
import { CalendarDateTime, parseDateTime } from "@internationalized/date";
import { EError } from "@sc07-canvas/lib";
import { Hammer } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { Button } from "../components/core/Button";
import { UserCard } from "../components/Profile/UserCard";
import { SidebarBase } from "../components/SidebarBase";
import { handleError, oapi, useQuery } from "../lib/utils";
import { ModeratorUtils, useModerator } from "./Moderator";
import { ModeratorModule } from "./ModeratorModule";

export const UserModSidebar = () => {
  const { state, dispatch } = useModerator();

  return (
    <SidebarBase
      shown={Boolean(state.inspectUser)}
      setSidebarShown={(val) => {
        if (!val) dispatch({ action: "INSPECT_USER" });
      }}
      icon={Hammer}
      title="Moderator: User Inspect"
      side="Left"
    >
      <div className="p-2">
        {state.inspectUser ? <Inner sub={state.inspectUser} /> : <></>}
      </div>
    </SidebarBase>
  );
};

const Inner = ({ sub }: { sub: string }) => {
  const userReq = useQuery("/user/{sub}", {
    params: {
      path: {
        sub,
      },
    },
  });
  const ipReq = useQuery("/mod/user/{sub}/ips", {
    params: {
      path: {
        sub,
      },
    },
  });

  return (
    <>
      <UserCard user={userReq.data?.user} hide={["MOD_ACTIONS"]} />
      <div className="my-1">
        <Button
          showAnchorIcon
          as={Link}
          target="_blank"
          href={ModeratorUtils.url("user", sub)}
          className="w-full"
          size="sm"
        >
          Open in admin
        </Button>
      </div>
      <Accordion>
        <AccordionItem title={`IP Addresses (${ipReq.data?.ips.length})`}>
          <ul className="list-disc pl-4">
            {ipReq.data?.ips.map((ip) => (
              <IPLine ip={ip} key={ip.ip} />
            ))}
          </ul>
        </AccordionItem>
        <AccordionItem title="Ban">
          <Ban sub={sub} />
        </AccordionItem>
        <AccordionItem title="Send Notice">
          <Notice sub={sub} />
        </AccordionItem>
        <AccordionItem title="Sockets & Captcha">
          <Captcha sub={sub} />
        </AccordionItem>
      </Accordion>
    </>
  );
};

type CaptchaStatus = "WAITING" | "INVALID" | "PASSED";
const CaptchaStatusColors: {
  [k in CaptchaStatus]:
    | "default"
    | "success"
    | "warning"
    | "primary"
    | "secondary"
    | "danger";
} = {
  WAITING: "default",
  INVALID: "danger",
  PASSED: "success",
};

const Captcha = ({ sub }: { sub: string }) => {
  const [status, setStatus] = useState<{
    [k: string]: CaptchaStatus;
  }>({});
  const [socketStatus, setSocketStatus] = useState<{
    [k: string]: "NEW" | "GONE";
  }>({});

  const sockets = useQuery("/mod/user/{sub}/sockets", {
    params: {
      path: {
        sub,
      },
    },
  });

  const sendCaptcha = useCallback(
    (socketId?: string) => {
      oapi.POST(
        socketId
          ? "/mod/user/{sub}/captcha/{socket}"
          : "/mod/user/{sub}/captcha",
        {
          params: {
            path: {
              sub,
              socket: socketId,
            },
          },
        },
      );
    },
    [sub],
  );

  useEffect(() => {
    console.log("captcha & sockets loaded");

    setStatus({});
    const handleStatus = (
      _id: number,
      _userId: string,
      socketId: string,
      status: CaptchaStatus,
    ) => {
      setStatus((v) => ({
        ...v,
        [socketId]: status,
      }));
    };

    const handleConnect = (id: string) => {
      setSocketStatus((v) => ({
        ...v,
        [id]: "NEW",
      }));
    };

    const handleDisconnect = (id: string) => {
      setSocketStatus((v) => ({
        ...v,
        [id]: "GONE",
      }));
    };

    const mod = ModeratorModule.get();

    mod.socket.emit("join", "captcha");
    mod.socket.emit("join", "user:" + sub);
    mod.socket.on("captcha", handleStatus);
    mod.socket.on("socketConnect", handleConnect);
    mod.socket.on("socketDisconnect", handleDisconnect);

    return () => {
      console.log("captcha & sockets unloaded");
      mod.socket.emit("leave", "captcha");
      mod.socket.emit("leave", "user:" + sub);
      mod.socket.off("captcha", handleStatus);
      mod.socket.off("socketConnect", handleConnect);
      mod.socket.off("socketDisconnect", handleDisconnect);
    };
  }, [sub]);

  return (
    <>
      <Button onPress={() => sendCaptcha()}>Send All</Button>
      <Accordion>
        {sockets.data?.sockets.map((socket) => (
          <AccordionItem
            key={socket.id}
            title={
              <>
                {socket.id} <CaptchaChip status={status[socket.id]} />
                <SocketChip status={socketStatus[socket.id]} />
              </>
            }
          >
            <Button size="sm" onPress={() => sendCaptcha(socket.id)}>
              Send Captcha
            </Button>
          </AccordionItem>
        )) || null}
      </Accordion>
    </>
  );
};

const SocketChip = ({ status }: { status?: "NEW" | "GONE" }) => {
  switch (status) {
    case "NEW":
      return (
        <Chip size="sm" color="warning">
          New
        </Chip>
      );
    case "GONE":
      return (
        <Chip size="sm" color="danger">
          Gone
        </Chip>
      );
    default:
      <></>;
  }
};

const CaptchaChip = ({ status }: { status?: CaptchaStatus }) => {
  if (!status) return <></>;
  return (
    <Chip size="sm" color={CaptchaStatusColors[status]}>
      {status}
    </Chip>
  );
};

const Notice = ({ sub }: { sub: string }) => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const doSend = useCallback(() => {
    setLoading(true);
    oapi
      .POST("/mod/user/{sub}/notice", {
        params: {
          path: {
            sub,
          },
        },
        body: {
          title,
          body: body || undefined,
        },
      })
      .then(({ error, response }) => {
        if (response.ok) {
          toast.success("Sent notice successfully");
          setTitle("");
          setBody("");
        } else {
          handleError(new EError(error, response.status));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [body, sub, title]);

  return (
    <>
      <Input
        label="Title of message"
        value={title}
        onValueChange={setTitle}
        isRequired
      />
      <Input label="Body of message" value={body} onValueChange={setBody} />
      <Button
        isLoading={loading}
        isDisabled={title.length < 5}
        onPress={doSend}
        color="primary"
      >
        Send Notice
      </Button>
    </>
  );
};

const Ban = ({ sub }: { sub: string }) => {
  const [lock, setLock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expiresAt, setExpiresAt] = useState<CalendarDateTime | null>();
  const [privateNote, setPrivateNote] = useState("");
  const [publicNote, setPublicNote] = useState("");

  const doPrepare = useCallback(() => {
    setLock(true);
  }, []);

  const doBan = useCallback(() => {
    if (!expiresAt) return;

    setLoading(true);
    oapi
      .PUT("/mod/user/{sub}/ban", {
        params: {
          path: {
            sub,
          },
        },
        body: {
          expiresAt: expiresAt!.toString(),
          privateNote: privateNote || undefined,
          publicNote: publicNote || undefined,
        },
      })
      .then(({ data, error, response }) => {
        if (data) {
          toast.success("Banned user successfully");
        }
        if (error) {
          handleError(new EError(error, response.status));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [expiresAt, privateNote, publicNote, sub]);

  return (
    <>
      <DateInput
        isRequired
        isDisabled={lock}
        granularity="minute"
        label="Expires At"
        value={expiresAt}
        onChange={setExpiresAt}
        minValue={parseDateTime(new Date().toISOString().slice(0, -1))}
      />
      <Input
        isDisabled={lock}
        label="Private Note"
        description="A note visible to only staff members"
        value={privateNote}
        onValueChange={setPrivateNote}
      />
      <Input
        isDisabled={lock}
        label="Public Note"
        description={
          <span className="text-danger-500">Note visible to user</span>
        }
        value={publicNote}
        onValueChange={setPublicNote}
      />
      <div className="flex gap-4 items-center">
        <Button
          color="primary"
          isDisabled={lock || !expiresAt}
          isLoading={lock}
          onPress={doPrepare}
        >
          Ban...
        </Button>
        {lock && (
          <>
            <Button color="default" onPress={() => setLock(false)}>
              Cancel
            </Button>
            <Button color="danger" onPress={doBan} isLoading={loading}>
              Confirm Ban
            </Button>
          </>
        )}
      </div>
    </>
  );
};

const IPLine = ({
  ip,
}: {
  ip: { ip: string; lastUsedAt: string; createdAt: string };
}) => {
  const [overview, setOverview] = useState<{ users: number }>({ users: 0 });

  useEffect(() => {
    oapi
      .GET("/mod/ip", {
        params: {
          query: {
            address: ip.ip,
          },
        },
      })
      .then(({ data }) => {
        if (data) {
          setOverview(data);
        }
      });
  }, [ip]);

  return (
    <li>
      {ip.ip}
      <Chip size="sm">{overview.users} Users</Chip>
      <Link target="_blank" href={ModeratorUtils.url("ip_lookup", ip.ip)}>
        Lookup
      </Link>
    </li>
  );
};
